// ElectronicContract.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ReactSignatureCanvas from "react-signature-canvas";
import { Document, Page, pdfjs } from "react-pdf";
import "core-js/full/promise/with-resolvers.js";
import "pdfjs-dist/webpack";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";

import { Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import {
    computeAgeText,
    computeYoungestAge,
    dateFormatDateTime,
    driverRangeText,
    formatDate,
    formatIdNumber,
    formatIdNumberBackNumber,
    formatInsuranceCurrency,
    formatTime,
    handleCommonFileChange,
    onlyNumericInput,
    useFormInput,
    formatNumberWithCommas,
} from "@/utils";
import { AuthCodeStatus } from "@/enum";
import {
    daeinPay1Options,
    daeinPay2Options,
    displacementOptions,
    driverTypeOptions,
    emergencyServiceOptions,
    jasonOptions,
    localCodeOptions,
    mobileTypeOptions,
    noInsInjuryOptions,
    selfCarDamageAmountOptions,
    selfCarDamageRateOptions,
    vehicleTypeOptions,
} from "@/constants/selectOptions";

import { useNicePhoneRequestMutation, useNicePhoneVerifyMutation } from "@/redux/authApi";
import { usePostJoinMutation } from "@/redux/joinApi";
import {
    useAgreeToTermsMutation,
    useCheckCustomerInfoMutation,
    useGetCustomerContractQuery,
    useLazyGetCustomerContractQuery,
    useSignContractMutation,
} from "@/redux/csContractApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import { useVerifyDriverLicenseMutation } from "@/redux/driverLicenseApi";
import logoImg from "@/assets/icons/icon-mobilicar-logo_bk.png";
import errorIcon from "@/assets/icons/icon-error.svg";
import successImg from "@/assets/images/sign_complete.png";
import timeoutImg from "@/assets/images/sign_timeout.png";
import successCheckIcon from "@/assets/icons/icon-check_success.svg";
import beforeIcon from "@/assets/icons/icon-nav_before.svg";
import closeIcon from "@/assets/icons/close-24.svg";
import identificationIcon from "@/assets/images/identification.png";

import { useGetInsuranceCorpQuery } from "../../redux/insuranceCorpApi";
import { useGetCarManufacturerQuery, useGetCarModelQuery } from "../../redux/metaApi";

// PDF.js 워커 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const ElectronicContract = () => {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(null);

    // 전역 폼 상태
    const { formData, handleChange, setFormData } = useFormInput({
        // 팝업 / 토스트
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        termViewPopupStatus: false,
        signaturePadPopupStatus: false,
        carImagesPopupStatus: false,

        // 약관 보기 시, 해당 약관 pdf의 objectUrl
        selectedTermPdfUrl: "",

        // Step 0: 고객 정보 (이름, 휴대폰 등) + token
        token: "",

        // Step 1: 이용약관 동의
        terms: {
            all: false,
            service: false,
            privacy: false,
            corporate: false,
            promotion: false,
        },

        // Step 2: 서명
        signedImage: null,

        // Step 3: 작성 폼
        loginId: "",
        password: "",
        passwordCheck: "",
        name: "",
        idNumber: "", // 주민번호
        mobileType: "",
        phone: "",
        phoneAuthId: null,
        authNum: "",
        authVerified: false,
        timer: 0,
        authCodeStatus: AuthCodeStatus.WAITING,

        // Step 4+ etc.
        authAbleYn: true,

        insuranceCorpSelectOptions: [],
    });

    // RTK Query hooks
    const {
        data: contractData,
        isLoading: isDetailLoading,
        isError: isDetailError,
        error: detailError,
    } = useGetCustomerContractQuery(contractId, {
        refetchOnMountOrArgChange: true,
    });

    const [postJoin, { isLoading: isJoinLoading }] = usePostJoinMutation();
    const [checkCustomerInfo] = useCheckCustomerInfoMutation();

    const [agreeToTerms] = useAgreeToTermsMutation();
    const [signContract] = useSignContractMutation();
    const [uploadFile] = useUploadFileMutation();

    useEffect(() => {
        if (contractData && contractData.data) {
            const detail = contractData.data;

            // 계약 상태에 따라 단계 설정
            if (detail?.contract?.contractStatus === "SIGN_FINISH") {
                setCurrentStep("SIGN_FINISH");
                return;
            } else {
                const currentTime = new Date().getTime();
                const expireDateTime = new Date(detail.expireDateTime).getTime();
                if (currentTime > expireDateTime) {
                    setCurrentStep("SIGN_TIMEOUT");
                    return;
                } else {
                    setCurrentStep(0);
                }
            }

            const originalContractGroup = detail.contractGroup || {};

            // contractGroup 내 driver 데이터 변환
            const mappedDrivers = (detail.contractDrivers || []).map((driver) => {
                let updated = { ...driver };
                // driverLicenseNumber가 12자리 이상이면,
                // 앞 2자리를 Number 타입으로 localCode에 저장하고 나머지 10자리를 재설정
                if (updated.driverLicenseNumber && updated.driverLicenseNumber.length >= 12) {
                    updated.localCode = Number(updated.driverLicenseNumber.slice(0, 2));
                    updated.driverLicenseNumber = updated.driverLicenseNumber.slice(2);
                }
                return updated;
            });
            const mainDriverInfo = mappedDrivers.filter((driver) => driver.contractDriverType === "MAIN");
            console.log(mainDriverInfo);

            // 추가 운전자 및 사업자 여부 체크
            const additionalDriverExists = mappedDrivers.some((driver) => driver.contractDriverType === "ADDITIONAL");
            const businessStatus = detail?.contractGroup?.contractBusinessInfo ? true : false;

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                contract: {
                    ...detail,
                    contractGroup: {
                        ...originalContractGroup,
                    },
                    contractDrivers: mappedDrivers,
                },
                idNumber: mainDriverInfo && mainDriverInfo.length > 0 ? mainDriverInfo[0].idNumber : "",
                additionalDriverStatus: additionalDriverExists,
                businessStatus: businessStatus,
            }));
        }
        if (isDetailError) {
            setCurrentStep("NOT_FOUND");
        }
    }, [contractData, setFormData, isDetailError]);

    const nextStep = () => setCurrentStep((prev) => prev + 1);
    const prevStep = () => setCurrentStep((prev) => prev - 1);

    const canGoNext = !!formData.signedImage;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    return (
        <>
            <Helmet>
                <title>모빌리카 전자계약서</title>
                <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
            </Helmet>

            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}

            <div className="mobile-container">
                <div className="mobile-content">
                    {currentStep === "NOT_FOUND" && (
                        <ContractNotFound
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                        />
                    )}

                    {currentStep === "SIGN_TIMEOUT" && (
                        <ContractTimeout
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                        />
                    )}

                    {currentStep === 0 && <ContractReceived navigate={navigate} formData={formData} nextStep={nextStep} />}

                    {currentStep === 1 && (
                        <IdentificationCheck
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}

                    {currentStep === 2 && (
                        <ContractTerms
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}

                    {currentStep === 3 && (
                        <RentalVehicleInfo
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}

                    {currentStep === 4 && (
                        <CustomerInputForm
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}

                    {currentStep === 5 && (
                        <AccidentVehicleInfo
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            uploadFile={uploadFile}
                        />
                    )}

                    {currentStep === 6 && (
                        <ContractSigning
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                            canGoNext={canGoNext}
                        />
                    )}

                    {currentStep === 7 && (
                        <StrictIdentificationCheck
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}

                    {currentStep === 8 && (
                        <ContractComplete
                            contractId={contractId}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            navigate={navigate}
                        />
                    )}
                </div>
            </div>

            {/* 약관 보기 팝업 */}
            {formData.termViewPopupStatus && (
                <TermViewPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, termViewPopupStatus: false }))}
                    formData={formData}
                    setFormData={setFormData}
                />
            )}

            {/* 차량 이미지 팝업 */}
            {formData.carImagesPopupStatus && (
                <CarImagesPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, carImagesPopupStatus: false }))}
                    formData={formData}
                />
            )}

            {/* 서명 팝업 */}
            {formData.signaturePadPopupStatus && (
                <SignaturePadPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, signaturePadPopupStatus: false }))}
                    formData={formData}
                    setFormData={setFormData}
                />
            )}

            {/* 주소 검색 팝업 */}
            {formData.addressPopupStatus && (
                <SearchAddress
                    modalStatus={formData.addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => {
                            let newState = {
                                ...prev,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                            switch (prev.addressPopupTarget) {
                                case "MAIN":
                                case "ADDITIONAL":
                                    newState.contract = {
                                        ...prev.contract,
                                        contractDrivers: prev.contract.contractDrivers.map((driver) => {
                                            if (driver.contractDriverType === prev.addressPopupTarget) {
                                                return {
                                                    ...driver,
                                                    address: data.address,
                                                    bCode: data.bcode,
                                                };
                                            }
                                            return driver;
                                        }),
                                    };
                                    break;
                                case "BIZ":
                                    newState.contract = {
                                        ...prev.contract,
                                        contractBusinessInfo: {
                                            ...prev.contract.contractGroup.contractBusinessInfo,
                                            address: data.address,
                                            bCode: data.bcode,
                                        },
                                    };
                                    break;
                                case "DELIVERY":
                                    newState.contract = {
                                        ...prev.contract,
                                        deliveryAddress: data.address,
                                        deliveryAddressBCode: data.bcode,
                                    };
                                    break;
                                default:
                                    break;
                            }
                            return newState;
                        })
                    }
                />
            )}
        </>
    );
};

// =======================================
// Step 0: ContractReceived
// =======================================
const ContractReceived = ({ navigate, formData, nextStep }) => {
    return (
        <div className="app-mobile">
            <div className="page-header">
                <span className="header-logo">
                    <img src={logoImg} alt="" />
                </span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-description">
                            <span className="description-main_txt">안녕하세요 :)</span>
                            <span className="description-sub_txt">
                                <em>{formData.contract.rentCorp.corpName || "렌터카 업체"}</em> 에서 <br />
                                계약서가 도착했습니다.
                            </span>
                            <p>
                                차량 정보를 확인해주시고, 고객님의 정보 및 서명을 <br />
                                해주시면 간편하게 대여 차량을 이용하실 수 있습니다.
                            </p>
                            <span className="expiration-time">
                                서명 가능 시간 (
                                {dateFormatDateTime(
                                    new Date(new Date(formData?.contract?.expireDateTime).getTime() + 24 * 60 * 60 * 1000),
                                    "YYYY-MM-DD HH:mm"
                                )}
                                )
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className="btn btn-primary btn-md button-login" onClick={nextStep}>
                            계약 시작하기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 1: IdentificationCheck
// =======================================
const IdentificationCheck = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep }) => {
    const [checkCustomerInfo] = useCheckCustomerInfoMutation();
    const [fetchContract, { data: contractData, error: contractError }] = useLazyGetCustomerContractQuery();

    // 1) 유효성 검사
    const validateForm = () => {
        // if (!formData.name) {
        //     setFormData((prev) => ({ ...prev, toastMsg: "이름을 입력해주세요." }));
        //     return false;
        // }
        if (!formData.phone) {
            setFormData((prev) => ({ ...prev, toastMsg: "휴대폰 번호를 입력해주세요." }));
            return false;
        }
        return true;
    };

    // 2) checkCustomerInfo API 호출
    const handleNext = async () => {
        if (!validateForm()) return;

        try {
            const body = {
                customerName: formData.name,
                customerPhone: formData.phone,
            };
            const response = await checkCustomerInfo({ contractId, body }).unwrap();

            localStorage.setItem("access_token", response.access_token);
            setFormData((prev) => ({
                ...prev,
                token: response.access_token,
            }));

            // 3) 인증 성공시 계약 상세조회
            // const contractResp = await fetchContract(contractId).unwrap();
            // console.log(contractResp.data);

            // setFormData((prev) => ({
            //     ...prev,
            //     contract: contractResp.data,
            // }));

            nextStep();
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "인증에 실패했습니다.",
            }));
        }
    };

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">고객정보 확인</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-description" style={{ paddingTop: "10px", alignItems: "center" }}>
                            <div className="description-img">
                                <img src={identificationIcon} alt="" />
                            </div>
                        </div>

                        {/* 이름 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">이름</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="이름을 입력해주세요."
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 휴대폰 번호 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">휴대폰 번호</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="number"
                                                placeholder="-없이 휴대폰번호 입력"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                onInput={onlyNumericInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className="btn btn-md btn-primary" onClick={handleNext}>
                            본인 인증하기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 2: ContractTerms
// =======================================
const ContractTerms = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep }) => {
    const contractTerms = formData.contract?.contractGroup?.contractTerms || [];

    const [agreeToTerms] = useAgreeToTermsMutation();

    // 필수 항목 (Y) 들 중에서 체크 안 된 게 있다면 다음 불가
    const mandatoryIds = contractTerms.filter((term) => term.mandatoryYn === "Y").map((term) => term.id);
    const checkedTerms = formData.checkedTerms || [];

    // 필수약관이 모두 checkedTerms에 포함되어 있는지 체킹
    const allRequiredChecked = mandatoryIds.every((id) => checkedTerms.includes(id));

    // 약관 체크 on/off
    const handleTermCheck = (termId, checked) => {
        setFormData((prev) => {
            let newChecked = [...(prev.checkedTerms || [])];
            if (checked) {
                // 추가
                if (!newChecked.includes(termId)) newChecked.push(termId);
            } else {
                // 제거
                newChecked = newChecked.filter((id) => id !== termId);
            }
            return { ...prev, checkedTerms: newChecked };
        });
    };

    // 전체동의 (모든 약관 체크)
    const handleAllCheck = (e) => {
        const isChecked = e.target.checked;
        if (isChecked) {
            // 모든 termId를 checked
            const allIds = contractTerms.map((t) => t.id);
            setFormData((prev) => ({ ...prev, checkedTerms: allIds }));
        } else {
            // 해제
            setFormData((prev) => ({ ...prev, checkedTerms: [] }));
        }
    };

    // "다음" 버튼
    const handleNext = async () => {
        if (!allRequiredChecked) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "모든 필수 약관에 동의해주세요.",
            }));
            return;
        }

        // 1) 약관 동의 완료 API
        try {
            await agreeToTerms(contractId).unwrap();

            nextStep();
        } catch (error) {
            console.error("약관 동의 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "약관 동의 처리 실패",
            }));
        }
    };

    // "전체 동의" 체크박스 상태: 모든 약관 ID가 checkedTerms에 들어있는지 체킹
    const isAllChecked = contractTerms.length > 0 && contractTerms.every((term) => (formData.checkedTerms || []).includes(term.id));

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">약관동의</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="term-container">
                            <div className="term-head">
                                <span className="head-tit">약관동의</span>
                                <p>
                                    대여 서비스 및 전자계약을 위해 <br />
                                    약관에 동의해주세요.
                                </p>
                            </div>
                            <div className="term-content">
                                <div className="term-list term-all">
                                    <div className="term-item">
                                        <div className="checkbox">
                                            <input type="checkbox" id="all" checked={isAllChecked} onChange={handleAllCheck} />
                                            <label htmlFor="all">
                                                <span>전체동의</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* 약관 리스트 */}
                                <div className="term-list">
                                    {contractTerms.map((term) => {
                                        const isMandatory = term.mandatoryYn === "Y";
                                        const isChecked = (formData.checkedTerms || []).includes(term.id);

                                        return (
                                            <div className="term-item" key={term.id}>
                                                <div className="checkbox">
                                                    <input
                                                        type="checkbox"
                                                        id={`term_${term.id}`}
                                                        checked={isChecked}
                                                        onChange={(e) => handleTermCheck(term.id, e.target.checked)}
                                                    />
                                                    <label htmlFor={`term_${term.id}`}>
                                                        <span>
                                                            {term.name}&nbsp;
                                                            {isMandatory === true ? (
                                                                <em>필수</em>
                                                            ) : isMandatory === false ? (
                                                                <b>선택</b>
                                                            ) : null}
                                                        </span>
                                                    </label>

                                                    {term?.upload?.objectUrl && (
                                                        <a
                                                            onClick={() => {
                                                                setFormData((prev) => ({
                                                                    ...prev,
                                                                    termViewPopupStatus: true,
                                                                    selectedTermPdfUrl: term.upload.objectUrl,
                                                                }));
                                                            }}
                                                        >
                                                            보기
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button
                            className={`btn btn-md button-login ${allRequiredChecked ? "btn-primary" : "btn-disabled"}`}
                            onClick={handleNext}
                        >
                            다음
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 3: RentalVehicleInfo
// =======================================
const RentalVehicleInfo = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep }) => {
    const handleNext = async () => {
        try {
            nextStep();
        } catch (error) {
            console.error(error);
            nextStep();
        }
    };

    // 금액을 만원 단위로 변환하는 함수
    const formatAmountToWon = (amount) => {
        if (amount === 0) return "없음";
        if (amount === -1) return "무한";
        if (!amount) return "없음";

        // 만원 단위로 변환
        const manwon = amount / 10000;

        // 콤마 포맷팅
        const formatted = manwon.toLocaleString();

        return formatted + "만원";
    };

    // 보험 가입 연령 변환
    const getApplicableAgeText = (age) => {
        if (!age) return "없음";
        return `만 ${age}세 이상`;
    };

    // 자차한도 구분 변환
    const getSelfCarLimitTypeText = (type) => {
        if (type === "PRICE") return "금액";
        if (type === "PERCENT") return "비율";
        return "없음";
    };

    // 자차한도 값 변환 (타입에 따라)
    const getSelfCarLimitValueText = (type, value) => {
        if (type === "PRICE") {
            return formatAmountToWon(value);
        } else if (type === "PERCENT") {
            if (value === 0) return "없음";
            if (value === -1) return "무한";
            return `${value}%`;
        }
        return "없음";
    };

    const getInsuranceRows = (insuranceItems) => {
        // joinYn이 "Y"인 항목만 필터링
        const joinedItems = insuranceItems.filter((item) => item.joinYn === "Y");
        console.log(joinedItems);
        const rows = [];

        // 대인배상: DAEIN_PAY_1과 DAEIN_PAY_2를 그룹화
        const daein1 = joinedItems.find((item) => item.insuranceItemType === "DAEIN_PAY_1");
        const daein2 = joinedItems.find((item) => item.insuranceItemType === "DAEIN_PAY_2");
        if (daein1 || daein2) {
            let range1 = "";
            if (daein1 && daein1.limitType) {
                const option = daeinPay1Options.find((o) => o.value === daein1.limitType);
                range1 = option ? option.label : "";
            }
            let range2 = "";
            if (daein2 && daein2.compensationRange) {
                const option = daeinPay2Options.find((o) => o.value === daein2.compensationRange);
                range2 = option ? option.label : "";
            }
            const compensationRange = [range1, range2].filter(Boolean).join(" / ");
            const selfPay = daein1 ? daein1.selfPay : daein2 ? daein2.selfPay : null;
            rows.push({
                type: "대인배상",
                compensationRange,
                selfPay: formatInsuranceCurrency(selfPay),
            });
        }

        // 자기차량손해: SELF_CAR_DAMAGE -> "자차"
        const selfCar = joinedItems.find((item) => item.insuranceItemType === "SELF_CAR_DAMAGE");
        if (selfCar) {
            let compensationRange = "";
            if (selfCar.selfCarDamageRangeType === "RATE") {
                const option = selfCarDamageRateOptions.find((o) => o.value === selfCar.selfCarDamageRateType);
                compensationRange = option ? option.label : "";
            } else if (selfCar.selfCarDamageRangeType === "PRICE" && selfCar.compensationRange) {
                const option = selfCarDamageAmountOptions.find((o) => o.value === selfCar.compensationRange);
                compensationRange = option ? option.label : "";
            }
            rows.push({
                type: "자차",
                compensationRange,
                selfPay: formatInsuranceCurrency(selfCar.selfPay),
            });
        }

        // 긴급출동: EMERGENCY_SERVICE -> "긴급출동"
        const emergency = joinedItems.find((item) => item.insuranceItemType === "EMERGENCY_SERVICE");
        if (emergency) {
            const option = emergencyServiceOptions.find((o) => o.value === emergency.compensationRange);
            const compensationRange = option ? option.label : "";
            // 긴급출동의 selfPay는 한글 컨버팅 없이 숫자 뒤에 " km"를 붙임
            rows.push({
                type: "긴급출동",
                compensationRange,
                selfPay: emergency.selfPay + "km",
            });
        }

        // 자손(사망,부상,장해): JASON_DEAD, JASON_INJURY, JASON_BLOCK 그룹화
        const jasonItems = joinedItems.filter((item) => ["JASON_DEAD", "JASON_INJURY", "JASON_BLOCK"].includes(item.insuranceItemType));
        if (jasonItems.length > 0) {
            const labels = jasonItems
                .map((item) => {
                    if (item.compensationRange) {
                        const option = jasonOptions.find((o) => o.value === item.compensationRange);
                        return option ? option.label : "";
                    }
                    return "";
                })
                .filter(Boolean);
            const compensationRange = labels.join(" / ");
            const selfPay = jasonItems[0].selfPay;
            rows.push({
                type: "자손(사망,부상,장해)",
                compensationRange,
                selfPay: formatInsuranceCurrency(selfPay),
            });
        }

        // 무보험차상해: NO_INS_INJURY
        const noIns = joinedItems.find((item) => item.insuranceItemType === "NO_INS_INJURY");
        if (noIns) {
            const option = noInsInjuryOptions.find((o) => o.value === noIns.compensationRange);
            const compensationRange = option ? option.label : "";
            rows.push({
                type: "무보험차상해",
                compensationRange,
                selfPay: formatInsuranceCurrency(noIns.selfPay),
            });
        }

        return rows;
    };

    const rows = getInsuranceRows(formData?.contract?.insuranceItems);

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">대여차량 정보</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="rental-car-info">
                        {formData?.contract.carImages && formData.contract.carImages.length > 0 ? (
                            <div className="car-img">
                                <Swiper
                                    modules={[Pagination]}
                                    pagination={{
                                        clickable: true,
                                        type: "fraction",
                                    }}
                                    spaceBetween={10}
                                    slidesPerView={1}
                                    style={{ width: "100%", height: "100%" }}
                                    loop={true}
                                >
                                    {formData.contract.carImages.map((img, idx) => (
                                        <SwiperSlide
                                            key={img.id || idx}
                                            onClick={() => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    carImagesPopupStatus: true,
                                                }));
                                            }}
                                        >
                                            <img
                                                src={img.upload?.objectUrl || img.upload?.url}
                                                alt={`car-${idx + 1}`}
                                                style={{ width: "100%", height: "auto", objectFit: "cover" }}
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        ) : (
                            formData?.contract.contractCar.modelImageUrl && (
                                <div className="car-img">
                                    <img src={formData?.contract.contractCar.modelImageUrl} alt="" />
                                </div>
                            )
                        )}

                        <div className="car-summary-info">
                            <div className="info-model_name">{`${formData?.contract.contractCar.manufacturerName || ""} ${
                                formData?.contract.contractCar.modelDetailName || ""
                            } ${formData?.contract.contractCar.className || ""} ${formData?.contract.contractCar.trimName || ""}`}</div>
                            <div className="info-options">
                                <div className="option-head">
                                    <img src={successCheckIcon} alt="" />
                                    <span className="head-tit">주요 옵션</span>
                                </div>
                                <div className="option-list">
                                    {formData?.contract.contractCar.options?.map((option, idx) => (
                                        <span className="option-item" key={idx}>
                                            {option.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="car-default-info">
                            <div className="info-head">
                                <span className="head-tit">기본 정보</span>
                            </div>
                            <div className="info-list">
                                <div className="item">
                                    <span className="item-tit">제조사</span>
                                    <span className="item-desc">{formData?.contract.contractCar.manufacturerName}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">차종</span>
                                    <span className="item-desc">{formData?.contract.contractCar.carGubun}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">차량번호</span>
                                    <span className="item-desc">{formData?.contract.contractCar.carNumber}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">연료</span>
                                    <span className="item-desc">{formData?.contract.contractCar.fuel}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">색상</span>
                                    <span className="item-desc">{formData?.contract.contractCar.color}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">연식</span>
                                    <span className="item-desc">{formData?.contract.contractCar.firstDate}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">변속기</span>
                                    <span className="item-desc">
                                        <em>{formData?.contract.contractCar.motorType}</em>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="car-insurance-info">
                            <div className="info-head">
                                <span className="head-tit">보험 정보</span>
                            </div>
                            <div className="info-list">
                                {/* 신규 contractInsuranceCondition이 있으면 신규 방식 사용 */}
                                {formData?.contract?.contractInsuranceCondition ? (
                                    <>
                                        {/* 일반 정보 */}
                                        <div className="item">
                                            <span className="item-tit">보험 가입 연령</span>
                                            <span className="item-desc">
                                                {getApplicableAgeText(formData.contract.contractInsuranceCondition.applicableAge)}
                                            </span>
                                        </div>
                                        <hr />
                                        {/* 자차 보험 */}
                                        <div className="item">
                                            <span className="item-tit">자차면책금</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.selfCarIndemnityPrice)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">자차한도 구분</span>
                                            <span className="item-desc">
                                                {getSelfCarLimitTypeText(formData.contract.contractInsuranceCondition.selfCarLimitType)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">자차한도</span>
                                            <span className="item-desc">
                                                {getSelfCarLimitValueText(
                                                    formData.contract.contractInsuranceCondition.selfCarLimitType,
                                                    formData.contract.contractInsuranceCondition.selfCarLimitValue
                                                )}
                                            </span>
                                        </div>

                                        <hr />

                                        {/* 종합 보험 */}
                                        <div className="item">
                                            <span className="item-tit">대인한도</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.bodilyInjuryLimitPrice)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">대물한도</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.propertyDamageLimitPrice)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">자손한도(부상)</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.personalInjuryLimitInjury)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">자손한도(사망)</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.personalInjuryLimitDeath)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">대인면책금(인당)</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.bodilyInjuryDeductible)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">대물면책금</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.propertyDamageDeductible)}
                                            </span>
                                        </div>
                                        <div className="item">
                                            <span className="item-tit">자손면책금</span>
                                            <span className="item-desc">
                                                {formatAmountToWon(formData.contract.contractInsuranceCondition.personalInjuryDeductible)}
                                            </span>
                                        </div>
                                    </>
                                ) : formData?.contract?.insuranceItems && formData.contract.insuranceItems.length > 0 ? (
                                    /* 기존 보험 정보 구조가 있는 경우 기존 방식(테이블) 사용 */
                                    <table>
                                        <colgroup>
                                            <col width={44} />
                                            <col width={28} />
                                            <col width={28} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th align="left">종류</th>
                                                <th align="center">보상범위</th>
                                                <th align="center">면책금</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td align="left">{row.type}</td>
                                                    <td align="center">{row.compensationRange}</td>
                                                    <td align="center">{row.selfPay}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="no-insurance-info">보험 정보가 없습니다.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className="btn btn-md btn-primary" onClick={handleNext}>
                            다음
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 4: CustomerInputForm
// =======================================
const CustomerInputForm = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep }) => {
    const [uploadFile] = useUploadFileMutation();

    // 체크박스 기본 활성화: 기존 데이터에 따라
    useEffect(() => {
        if (formData.contract) {
            const additionalExists = formData.contract.contractDrivers?.some((d) => d.contractDriverType === "ADDITIONAL");
            const bizExists = formData.contract.contractGroup.contractBusinessInfo?.corpName;
            if (additionalExists && !formData.additionalDriverStatus) {
                setFormData((prev) => ({ ...prev, additionalDriverStatus: true }));
            }
            if (bizExists && !formData.businessStatus) {
                setFormData((prev) => ({ ...prev, businessStatus: true }));
            }
        }
    }, [formData.contract, setFormData]);

    // 유효성 체크
    const validateForm = () => {
        // MAIN 운전자 검증
        const mainDriver = formData.contract?.contractDrivers?.find((d) => d.contractDriverType === "MAIN");
        if (!mainDriver || !mainDriver.name?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 이름을 입력해주세요." }));
            return false;
        }
        if (!mainDriver.idNumber?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 주민번호를 입력해주세요." }));
            return false;
        }
        if (mainDriver.idNumber?.trim().length !== 13) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 주민번호는 13자리여야 합니다." }));
            return false;
        }
        if (!mainDriver.phone?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 휴대폰 번호를 입력해주세요." }));
            return false;
        }
        // 면허 지역 검증
        if (!mainDriver.localCode) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 면허 지역을 선택해주세요." }));
            return false;
        }
        // 면허 번호 검증
        if (!mainDriver.driverLicenseNumber?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 면허 번호를 입력해주세요." }));
            return false;
        }
        if (mainDriver.driverLicenseNumber?.trim().length !== 10) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 면허 번호는 지역번호 12자리여야 합니다." }));
            return false;
        }
        const mainUpload = formData.contract?.contractGroup?.contractUploads?.find((u) => u.uploadType === "DRIVER_LICENSE_MAIN");
        // if (!mainUpload || !mainUpload.upload?.id) {
        //     setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 운전면허증 사진을 업로드해주세요." }));
        //     return false;
        // }
        // 면허검증 여부
        /*
        if (!mainDriver.licenseVerified) {
            setFormData((prev) => ({ ...prev, toastMsg: "운전자 정보: 면허검증이 필요합니다." }));
            return false;
        }
         */
        // 제2 운전자 검증 (체크된 경우)
        if (formData.additionalDriverStatus) {
            const additionalDriver = formData.contract?.contractDrivers?.find((d) => d.contractDriverType === "ADDITIONAL");
            if (!additionalDriver || !additionalDriver.name?.trim()) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 이름을 입력해주세요." }));
                return false;
            }
            if (!additionalDriver.idNumber?.trim()) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 주민번호를 입력해주세요." }));
                return false;
            }
            if (additionalDriver.idNumber?.trim().length !== 13) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 주민번호는 13자리여야 합니다." }));
                return false;
            }
            if (!additionalDriver.phone?.trim()) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 휴대폰 번호를 입력해주세요." }));
                return false;
            }
            // 제2 운전자 면허 지역 검증
            if (!additionalDriver.localCode) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 면허 지역을 선택해주세요." }));
                return false;
            }
            // 제2 운전자 면허 번호 검증
            if (!additionalDriver.driverLicenseNumber?.trim()) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 면허 번호를 입력해주세요." }));
                return false;
            }
            if (additionalDriver.driverLicenseNumber?.trim().length !== 10) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 면허 번호는 지역번호 10자리여야 합니다." }));
                return false;
            }
            const subUpload = formData.contract?.contractGroup?.contractUploads?.find((u) => u.uploadType === "DRIVER_LICENSE_SUB");
            // if (!subUpload || !subUpload.upload?.id) {
            //     setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 운전면허증 사진을 업로드해주세요." }));
            //     return false;
            // }
            /*
            if (!additionalDriver.licenseVerified) {
                setFormData((prev) => ({ ...prev, toastMsg: "제2 운전자 정보: 면허검증이 필요합니다." }));
                return false;
            }
             */
        }
        // 사업자 소지 검증 (체크된 경우)
        /*
        if (formData.businessStatus) {
            const bizInfo = formData.contract?.contractGroup?.contractBusinessInfo;
            if (!bizInfo || !bizInfo.corpName?.trim()) {
                setFormData((prev) => ({ ...prev, toastMsg: "사업자 정보: 사업자명을 입력해주세요." }));
                return false;
            }
            const bizUpload = formData.contract?.contractGroup?.contractUploads?.find((u) => u.uploadType === "BIZ_LIC");
            if (!bizUpload || !bizUpload.upload?.id) {
                setFormData((prev) => ({ ...prev, toastMsg: "사업자 정보: 사업자등록증 사진을 업로드해주세요." }));
                return false;
            }
        }
         */

        // 탁송 정보
        /*
        if (!formData?.contract?.deliveryAddress) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량받으실 주소를 입력해주세요." }));
            return false;
        }
        if (!formData?.contract?.deliveryAddressDetail) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량받으실 상세 주소를 입력해주세요." }));
            return false;
        }
         */

        return true;
    };

    const handleNext = () => {
        if (validateForm()) {
            nextStep();
        }
    };

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">고객정보 입력</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-head">
                            <span className="head-tit">운전자 정보</span>
                        </div>

                        {/* 운전자 정보 */}
                        <DriverInputForm
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            driverType="MAIN"
                            handleCommonFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
                            uploadFile={uploadFile}
                        />

                        {/* 추가 입력 여부 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-check">
                                            <div className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="additionalDriverStatus"
                                                    name="additionalDriverStatus"
                                                    checked={formData.additionalDriverStatus}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFormData((prev) => {
                                                            // 기존 운전자 정보 배열 (존재하지 않으면 빈 배열)
                                                            const drivers = prev.contract?.contractDrivers || [];
                                                            let updatedDrivers = [...drivers];

                                                            if (checked) {
                                                                // 추가 운전자(ADDITIONAL)가 없으면 새 항목 추가
                                                                if (!updatedDrivers.some((d) => d.contractDriverType === "ADDITIONAL")) {
                                                                    updatedDrivers.push({
                                                                        id: null,
                                                                        contractDriverType: "ADDITIONAL",
                                                                        name: "",
                                                                        idNumberF: "",
                                                                        idNumberB: "",
                                                                        phone: "",
                                                                        address: "",
                                                                        addressDetail: "",
                                                                        driverLicenseType: "",
                                                                        driverLicenseNumber: "",
                                                                    });
                                                                }
                                                            } else {
                                                                // 체크 해제 시, 추가 운전자(ADDITIONAL) 항목 제거
                                                                updatedDrivers = updatedDrivers.filter(
                                                                    (d) => d.contractDriverType !== "ADDITIONAL"
                                                                );
                                                            }

                                                            return {
                                                                ...prev,
                                                                additionalDriverStatus: checked,
                                                                contract: {
                                                                    ...prev.contract,
                                                                    contractDrivers: updatedDrivers,
                                                                },
                                                            };
                                                        });
                                                    }}
                                                />
                                                <label htmlFor="additionalDriverStatus">
                                                    <span>제2 운전자</span>
                                                </label>
                                            </div>
                                            <div className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="businessStatus"
                                                    name="businessStatus"
                                                    checked={formData.businessStatus}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            businessStatus: checked,
                                                            contract: {
                                                                ...prev.contract,
                                                                contractGroup: {
                                                                    ...prev.contract.contractGroup,
                                                                    // 체크 해제 시 contractBusinessInfo 삭제 (null로 설정)
                                                                    contractBusinessInfo: checked
                                                                        ? prev.contract.contractGroup.contractBusinessInfo
                                                                        : null,
                                                                },
                                                            },
                                                        }));
                                                    }}
                                                />
                                                <label htmlFor="businessStatus">
                                                    <span>사업자 소지</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 제2 운전자 정보 섹션 */}
                    {formData.additionalDriverStatus && (
                        <div className="form-wrapper">
                            <div className="form-head">
                                <span className="head-tit">제2 운전자 정보</span>
                            </div>
                            <DriverInputForm
                                formData={formData}
                                handleChange={handleChange}
                                setFormData={setFormData}
                                driverType="ADDITIONAL"
                                handleCommonFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
                                uploadFile={uploadFile}
                            />
                        </div>
                    )}

                    {/* 사업자 정보 섹션 */}
                    {formData.businessStatus && (
                        <div className="form-wrapper">
                            <div className="form-head">
                                <span className="head-tit">사업자 정보</span>
                            </div>
                            <BizInputForm
                                formData={formData}
                                handleChange={handleChange}
                                setFormData={setFormData}
                                handleCommonFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
                            />
                        </div>
                    )}

                    <div className="form-wrapper">
                        <div className="form-head">
                            <span className="head-tit">차량 받으실 주소</span>
                        </div>
                        <ConsignmentInputForm formData={formData} handleChange={handleChange} setFormData={setFormData} />
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className="btn btn-md btn-primary" onClick={handleNext}>
                            다음
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DriverInputForm = ({ formData, handleChange, setFormData, driverType, handleRequestAuth, handleCommonFileChange, uploadFile }) => {
    const [verifyDriverLicense] = useVerifyDriverLicenseMutation();

    // formData.contract.contractDrivers 배열에서 해당 운전자 데이터 선택
    const driverData = formData.contract?.contractDrivers?.find((d) => d.contractDriverType === driverType) || {};

    // 업데이트 헬퍼: 해당 운전자 객체의 필드를 업데이트
    const updateDriverField = (field, value) => {
        setFormData((prev) => {
            const updatedDrivers =
                prev.contract?.contractDrivers?.map((driver) =>
                    driver.contractDriverType === driverType ? { ...driver, [field]: value } : driver
                ) || [];
            return {
                ...prev,
                contract: {
                    ...prev.contract,
                    contractDrivers: updatedDrivers,
                },
            };
        });
    };

    const handleLicenseVerification = async () => {
        if (!driverData.name?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "이름을 입력해주세요." }));
            return;
        }

        if (!driverData.driverLicenseType) {
            setFormData((prev) => ({ ...prev, toastMsg: "면허구분을 선택해주세요." }));
            return;
        }

        const isDirectNumber = driverData.licenseNumberType === "DIRECT";
        let licenseNumber = "";

        if (!driverData.licenseNumberType) {
            setFormData((prev) => ({ ...prev, toastMsg: "면허 구분을 선택해주세요." }));
            return;
        }

        if (isDirectNumber) {
            if (!driverData.driverLicenseNumber || driverData.driverLicenseNumber.length !== 12) {
                setFormData((prev) => ({ ...prev, toastMsg: "면허번호 숫자 12자리를 입력해주세요." }));
                return;
            }
            licenseNumber = driverData.driverLicenseNumber;
        } else {
            if (!driverData.localCode) {
                setFormData((prev) => ({ ...prev, toastMsg: "지역을 선택해주세요." }));
                return;
            }
            if (!driverData.driverLicenseNumber || driverData.driverLicenseNumber.length !== 10) {
                setFormData((prev) => ({ ...prev, toastMsg: "지역선택 시 면허번호 숫자 10자리를 입력해주세요." }));
                return;
            }
            licenseNumber = `${driverData.localCode}${driverData.driverLicenseNumber}`;
        }

        try {
            const verificationResponse = await verifyDriverLicense({
                name: driverData.name,
                license_number: licenseNumber,
                license_type: driverData.driverLicenseType,
            }).unwrap();

            setFormData((prev) => {
                const updatedDrivers =
                    prev.contract?.contractDrivers?.map((driver) => {
                        if (driver.contractDriverType === driverType) {
                            return { ...driver, licenseVerified: true };
                        }
                        return driver;
                    }) || [];
                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        contractDrivers: updatedDrivers,
                    },
                    toastMsg: "면허 검증 성공",
                };
            });
        } catch (error) {
            setFormData((prev) => ({ ...prev, toastMsg: error?.message || "면허 검증에 실패했습니다." }));
        }
    };

    // 파일 업로드 타입: MAIN은 "DRIVER_LICENSE_MAIN", 추가는 "DRIVER_LICENSE_SUB"
    const fileUploadType = driverType === "MAIN" ? "DRIVER_LICENSE_MAIN" : "DRIVER_LICENSE_SUB";
    const existingUpload = formData.contract?.contractGroup?.contractUploads?.find((u) => u.uploadType === fileUploadType);

    useEffect(() => {
        updateDriverField("licenseNumberType", "LOCAL");
    }, []);

    return (
        <>
            {/* 이름 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">이름</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="이름을 입력해주세요."
                                    name="name"
                                    value={driverData.name || ""}
                                    onChange={(e) => updateDriverField("name", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 주민번호 앞 7자리 및 계산된 나이 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">주민등록번호</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="주민등록번호 13자리 입력"
                                    name="idNumber"
                                    value={formatIdNumber(driverData.idNumber) || ""}
                                    onChange={(e) => updateDriverField("idNumber", e.target.value)}
                                    onInput={onlyNumericInput}
                                    maxLength={14}
                                />
                            </div>
                            <div className="field-row" style={{ width: "72px", flexShrink: "0" }}>
                                <input type="text" name="calculatedAge" value={computeAgeText(driverData)} disabled />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 휴대폰 번호 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">휴대폰 번호</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="-없이 휴대폰번호 입력"
                                    name="phone"
                                    value={driverData.phone || ""}
                                    onChange={(e) => updateDriverField("phone", e.target.value)}
                                    onInput={onlyNumericInput}
                                    disabled={driverType === "MAIN"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 면허 구분 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">면허 구분</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <Select
                                    options={driverTypeOptions}
                                    className="custom-select"
                                    classNamePrefix="custom-select"
                                    isSearchable={false}
                                    placeholder="면허 구분"
                                    value={driverTypeOptions.find((option) => option.value === driverData.driverLicenseType) || null}
                                    onChange={(option) => updateDriverField("driverLicenseType", option.value)}
                                />
                            </div>
                        </div>
                        {/* 라디오 버튼: 숫자 12자리 vs 지역+숫자 */}
                        <div className="content-field">
                            <div className="field-radio">
                                <div className="radiobox" style={{ display: "none" }}>
                                    <input
                                        type="radio"
                                        name={`licenseNumberType_${driverType}`}
                                        id={`licenseNumberType_DIRECT_${driverType}`}
                                        checked={driverData.licenseNumberType === "DIRECT"}
                                        onChange={() => updateDriverField("licenseNumberType", "DIRECT")}
                                    />
                                    <label htmlFor={`licenseNumberType_DIRECT_${driverType}`}>숫자 12자리</label>
                                </div>
                                <div className="radiobox" style={{ display: "none" }}>
                                    <input
                                        type="radio"
                                        name={`licenseNumberType_${driverType}`}
                                        id={`licenseNumberType_LOCAL_${driverType}`}
                                        checked={driverData.licenseNumberType === "LOCAL"}
                                        onChange={() => updateDriverField("licenseNumberType", "LOCAL")}
                                    />
                                    <label htmlFor={`licenseNumberType_LOCAL_${driverType}`}>지역+숫자</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 면허번호 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">면허번호</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            {/* 미선택 화면 */}
                            {!driverData.licenseNumberType && (
                                <div className="field-row">
                                    <input
                                        type="text"
                                        placeholder="면허 구분을 선택해주세요."
                                        name="driverLicenseNumber"
                                        value={driverData.driverLicenseNumber || ""}
                                        onInput={onlyNumericInput}
                                        maxLength={12}
                                        disabled
                                    />
                                </div>
                            )}
                            {/* 숫자 12자리 선택시 */}
                            {driverData.licenseNumberType === "DIRECT" && (
                                <div className="field-row">
                                    <input
                                        type="number"
                                        placeholder="-없이 면허번호 입력"
                                        name="driverLicenseNumber"
                                        value={driverData.driverLicenseNumber || ""}
                                        onChange={(e) => updateDriverField("driverLicenseNumber", e.target.value)}
                                        onInput={onlyNumericInput}
                                        maxLength={12}
                                    />
                                </div>
                            )}
                            {/* 지역+숫자 선택시 */}
                            {driverData.licenseNumberType === "LOCAL" && (
                                <>
                                    <div className="field-row">
                                        <Select
                                            options={localCodeOptions}
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable={false}
                                            placeholder="지역"
                                            value={localCodeOptions.find((option) => option.value === driverData.localCode) || null}
                                            onChange={(option) => updateDriverField("localCode", option.value)}
                                        />
                                    </div>
                                    <div className="field-row">
                                        <input
                                            type="number"
                                            placeholder="-없이 면허번호 입력"
                                            name="driverLicenseNumber"
                                            value={driverData.driverLicenseNumber || ""}
                                            onChange={(e) => updateDriverField("driverLicenseNumber", e.target.value)}
                                            onInput={onlyNumericInput}
                                            maxLength={10}
                                        />
                                    </div>
                                </>
                            )}
                            <button
                                type="button"
                                className="btn btn-xs btn-fourth"
                                onClick={handleLicenseVerification}
                                disabled={driverData.licenseVerified}
                            >
                                면허검증
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* 면허만기일 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">면허만기일</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <div className="input-esset">
                                    <img src="datepickerIcon.png" alt="" />
                                </div>
                                {/* <DatePicker
                                    locale={ko}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={20}
                                    selected={driverData.issueDate ? new Date(driverData.issueDate) : null}
                                    onChange={(date) => updateDriverField("issueDate", date.toISOString().split("T")[0])}
                                    dateFormat="yyyy.MM.dd"
                                    onKeyDown={(e) => e.preventDefault()}
                                /> */}
                                <input
                                    type="text"
                                    name="issueDate"
                                    value={formatDate(driverData.issueDate)}
                                    placeholder="ex) 20250101"
                                    onChange={(e) => updateDriverField("issueDate", e.target.value)}
                                    maxLength={10}
                                />
                            </div>
                        </div>
                        {/* <div className="content-field">
                            <div className="field-check">
                                <div className="checkbox">
                                    <input
                                        type="checkbox"
                                        name={`licenseOverYear_${driverType}`}
                                        id={`licenseOverYear_${driverType}`}
                                        checked={driverData.licenseOverYear === true}
                                        onChange={() => updateDriverField("licenseOverYear", true)}
                                    />
                                    <label htmlFor={`licenseOverYear_${driverType}`}>
                                        <span>
                                            면허 취득일이 1년 이상입니다. <br />
                                            <p className="txt-warning">1년 미만일 경우 대여가 어려울 수 있습니다.</p>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div> */}
                        <p className="txt-warning">면허취득이 1년이 안되었을 경우 렌트에 제한이 있을 수 있습니다. 업체와 상의하세요.</p>
                    </div>
                </div>
            </div>
            {/* 운전면허증 파일 업로드 */}
            {/* <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">운전면허증</span>
                    </div>
                    <FileUpload
                        uploadType={fileUploadType}
                        existingUpload={existingUpload}
                        buttonText="사진 업로드"
                        onFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
                    />
                </div>
            </div> */}
            {/* 주소 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">주소</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="bCode"
                                    value={driverData.bCode}
                                    onChange={handleChange}
                                    readOnly
                                    style={{ display: "none" }}
                                />
                                <input
                                    type="text"
                                    placeholder="주소 입력"
                                    name="address"
                                    value={driverData.address}
                                    onChange={(e) => updateDriverField("address", e.target.value)}
                                    readOnly
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            addressPopupStatus: true,
                                            addressPopupTarget: driverType,
                                        }))
                                    }
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-xs btn-primary"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        addressPopupStatus: true,
                                        addressPopupTarget: driverType,
                                    }))
                                }
                            >
                                검색
                            </button>
                        </div>
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="상세 주소"
                                    name="addressDetail"
                                    value={driverData.addressDetail}
                                    onChange={(e) => updateDriverField("addressDetail", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const BizInputForm = ({ formData, handleChange, setFormData, handleCommonFileChange, uploadFile }) => {
    const existingUpload = formData.contract?.contractGroup?.contractUploads?.find((u) => u.uploadType === "BIZ_LIC");

    return (
        <>
            {/* 사업자명 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">사업자명</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="사업자명을 입력해주세요."
                                    name="contract.contractGroup.contractBusinessInfo.corpName"
                                    value={formData?.contract?.contractGroup?.contractBusinessInfo?.corpName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 사업자등록번호 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">사업자등록번호</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="-없이 입력해주세요."
                                    name="contract.contractGroup.contractBusinessInfo.bizNumber"
                                    value={formData?.contract?.contractGroup?.contractBusinessInfo?.bizNumber}
                                    onChange={handleChange}
                                    onInput={onlyNumericInput}
                                    maxLength={10}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 사업자등록증 업로드 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">
                            <em>*</em> 사업자등록증
                        </span>
                    </div>
                    <FileUpload
                        uploadType="BIZ_LIC"
                        existingUpload={existingUpload}
                        buttonText="사진 업로드"
                        onFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
                    />
                </div>
            </div>
            {/* 주소 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">주소</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    name="contract.contractGroup.contractBusinessInfo.bCode"
                                    value={formData?.contract?.contractGroup?.contractBusinessInfo?.bCode}
                                    onChange={handleChange}
                                    readOnly
                                    style={{ display: "none" }}
                                />
                                <input
                                    type="text"
                                    placeholder="주소 입력"
                                    name="contract.contractGroup.contractBusinessInfo.address"
                                    value={formData?.contract?.contractGroup?.contractBusinessInfo?.address}
                                    onChange={handleChange}
                                    readOnly
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            addressPopupStatus: true,
                                            addressPopupTarget: "BIZ",
                                        }))
                                    }
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-xs btn-primary"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        addressPopupStatus: true,
                                        addressPopupTarget: "BIZ",
                                    }))
                                }
                            >
                                검색
                            </button>
                        </div>
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="상세 주소"
                                    name="contract.contractGroup.contractBusinessInfo.addressDetail"
                                    value={formData?.contract?.contractGroup?.contractBusinessInfo?.addressDetail}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const ConsignmentInputForm = ({ formData, handleChange, setFormData }) => {
    return (
        <>
            {/* 탁송지 주소 */}
            <div className="form-row">
                <div className="row-item">
                    <div className="item-label">
                        <span className="label-title">주소</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="bCode"
                                    name="contract.deliveryAddressBCode"
                                    value={formData?.contract?.deliveryAddressBCode}
                                    onChange={handleChange}
                                    readOnly
                                    style={{ display: "none" }}
                                />
                                <input
                                    type="text"
                                    placeholder="주소 입력"
                                    name="contract.deliveryAddress"
                                    value={formData?.contract?.deliveryAddress}
                                    onChange={handleChange}
                                    readOnly
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            addressPopupStatus: true,
                                            addressPopupTarget: "DELIVERY",
                                        }))
                                    }
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-xs btn-primary"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        addressPopupStatus: true,
                                        addressPopupTarget: "DELIVERY",
                                    }))
                                }
                            >
                                검색
                            </button>
                        </div>
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    placeholder="상세 주소"
                                    name="contract.deliveryAddressDetail"
                                    value={formData?.contract?.deliveryAddressDetail}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// =======================================
// Step 5: AccidentVehicleInfo
// =======================================
const AccidentVehicleInfo = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep, uploadFile }) => {
    const validateForm = () => {
        if (!formData?.contract?.contractGroup?.accidentCar?.carNumber) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량 번호를 입력해주세요." }));
            return false;
        }
        if (!formData?.contract?.contractGroup?.accidentCar?.bmNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량 제조사를 선택해주세요." }));
            return false;
        }
        if (!formData?.contract?.contractGroup?.accidentCar?.boiNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량 모델을 선택해주세요." }));
            return false;
        }
        if (!formData?.contract?.contractGroup?.contractInsurance?.insuranceCorpId) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험사를 선택해주세요." }));
            return false;
        }
        if (!formData?.contract?.contractGroup?.contractInsurance?.receiptNumber) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험 접수번호를 입력해주세요." }));
            return false;
        }
        return true;
    };

    const handleNext = async () => {
        if (!validateForm()) return;
        try {
            nextStep();
        } catch (error) {
            console.error(error);
            nextStep();
        }
    };

    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null, cho: formData.cho });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
            cho: item.cho,
        })) || [];

    // 모델 (skip: !manufacturer)
    const { data: modelData } = useGetCarModelQuery(
        { bmNo: formData?.contract?.contractGroup?.accidentCar?.bmNo, carGubun: formData.carGubun },
        { skip: !formData?.contract?.contractGroup?.accidentCar?.bmNo }
    );
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
            carGubun: item.carGubun,
        })) || [];

    const { data: insuranceCorpData } = useGetInsuranceCorpQuery({ menu_type_in: "CAR" });
    const insuranceCorpOptions =
        insuranceCorpData && insuranceCorpData.data
            ? insuranceCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">사고차량 정보</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-head">
                            <span className="head-tit">사고차량 정보</span>
                        </div>
                        {/* 차량 번호 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">차량 번호</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="123가1234"
                                                name="contract.contractGroup.accidentCar.carNumber"
                                                value={formData?.contract?.contractGroup?.accidentCar?.carNumber}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">제조사</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <Select
                                                options={manufacturerOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="제조사"
                                                value={
                                                    manufacturerOptions.find(
                                                        (option) => option.value === formData?.contract?.contractGroup?.accidentCar?.bmNo
                                                    ) || ""
                                                }
                                                onChange={(option) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        contract: {
                                                            ...prev.contract,
                                                            contractGroup: {
                                                                ...prev.contract.contractGroup,
                                                                accidentCar: {
                                                                    ...prev.contract.contractGroup.accidentCar,
                                                                    bmNo: option.value,
                                                                    boiNo: undefined,
                                                                },
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 모델 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">모델</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <Select
                                                options={modelOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="모델"
                                                value={
                                                    modelOptions.find(
                                                        (option) => option.value === formData?.contract?.contractGroup?.accidentCar?.boiNo
                                                    ) || ""
                                                }
                                                onChange={(option) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        contract: {
                                                            ...prev.contract,
                                                            contractGroup: {
                                                                ...prev.contract.contractGroup,
                                                                accidentCar: {
                                                                    ...prev.contract.contractGroup.accidentCar,
                                                                    boiNo: option.value,
                                                                },
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 배기량 & 차종 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">배기량</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="배기량"
                                                name="contract.contractGroup.accidentCar.displacement"
                                                value={formData?.contract?.contractGroup?.accidentCar?.displacement || ""}
                                                onChange={handleChange}
                                                onInput={onlyNumericInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">차종</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <Select
                                                options={vehicleTypeOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="차종"
                                                value={vehicleTypeOptions.find(
                                                    (option) => option.value === formData?.contract?.contractGroup?.accidentCar?.carGubun
                                                )}
                                                onChange={(option) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        contract: {
                                                            ...prev.contract,
                                                            contractGroup: {
                                                                ...prev.contract.contractGroup,
                                                                accidentCar: {
                                                                    ...prev.contract.contractGroup.accidentCar,
                                                                    carGubun: option.value,
                                                                },
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-wrapper">
                        <div className="form-head">
                            <span className="head-tit">사고차량 이미지</span>
                        </div>
                        {/* 사고차량 이미지 */}
                        <div className="form-col">
                            <div className="col-item">
                                <div className="item-label">
                                    <span className="label-title" style={{ fontSize: "15px", fontWeight: "600", color: "#323740" }}>
                                        차량 이미지
                                    </span>
                                    <span style={{ fontSize: "12px", color: "#666", marginLeft: "5px" }}>
                                        (최대 20개, png/jpg/jpeg만 가능)
                                    </span>
                                </div>
                                <div className="item-content" style={{ marginTop: "8px" }}>
                                    <div className="content-field">
                                        <div className="field-row">
                                            <AccidentCarImageUpload formData={formData} setFormData={setFormData} uploadFile={uploadFile} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-wrapper">
                        <div className="form-head">
                            <span className="head-tit">고객 보험 정보</span>
                        </div>
                        {/* 보험사 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">보험사</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <Select
                                                options={insuranceCorpOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="보험사"
                                                value={insuranceCorpOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        formData?.contract?.contractGroup?.contractInsurance?.insuranceCorpId
                                                )}
                                                onChange={(option) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        contract: {
                                                            ...prev.contract,
                                                            contractGroup: {
                                                                ...prev.contract.contractGroup,
                                                                contractInsurance: {
                                                                    ...prev.contract.contractGroup.contractInsurance,
                                                                    insuranceCorpId: option.value,
                                                                },
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 접수번호 */}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-label">
                                    <span className="label-title">접수번호</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="접수번호"
                                                name="contract.contractGroup.contractInsurance.receiptNumber"
                                                value={formData?.contract?.contractGroup?.contractInsurance?.receiptNumber}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className="btn btn-md btn-primary" onClick={handleNext}>
                            다음
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 6: AttachFileRegister (장기렌트)
// =======================================
// const AttachFileRegister = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep,handleCommonFileChange,uploadFile }) => {
//     const validateForm = () => {
//         if (!formData?.contract?.contractGroup?.accidentCar?.carNumber) {
//             setFormData((prev) => ({ ...prev, toastMsg: "차량 번호를 입력해주세요." }));
//             return false;
//         }
//         if (!formData?.contract?.contractGroup?.accidentCar?.modelName) {
//             setFormData((prev) => ({ ...prev, toastMsg: "차량 모델명을 입력해주세요." }));
//             return false;
//         }
//         return true;
//     };

//     const handleNext = async () => {
//         if (!validateForm()) return;

//         try {
//             nextStep();
//         } catch (error) {
//             console.error(error);
//             nextStep();
//         }
//     };

//     const fileUploadType = driverType === "MAIN" ? "DRIVER_LICENSE_MAIN" : "DRIVER_LICENSE_SUB";
//     const existingUpload = formData.contract?.contractUploads?.find((u) => u.uploadType === fileUploadType);

//     const existingUpload = formData.contract?.contractUploads?.find((u) => u.uploadType === "BIZ_LIC");
//     return (
//         <div className="app-mobile">
//             <div className="page-header">
//                 <button type="button" className="btn-nav_before" onClick={prevStep}>
//                     <img src={beforeIcon} alt="" />
//                 </button>
//                 <span className="header-tit">사고차량 정보</span>
//             </div>
//             <div className="page-content">
//                 <div className="content-form">
//                     <div className="form-wrapper">
//                         <div className="form-head">
//                             <span className="head-tit">사고차량 정보</span>
//                         </div>
//                         {/* 운전면허증 파일 업로드 */}
//                         <div className="form-row">
//                             <div className="row-item">
//                                 <div className="item-label">
//                                     <span className="label-title">
//                                         <em>*</em> 운전면허증
//                                     </span>
//                                 </div>
//                                 <FileUpload
//                                     uploadType={fileUploadType}
//                                     existingUpload={existingUpload}
//                                     buttonText="사진 업로드"
//                                     onFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
//                                 />
//                             </div>
//                         </div>
//                         {/* 사업자등록증 업로드 */}
//                         <div className="form-row">
//                             <div className="row-item">
//                                 <div className="item-label">
//                                     <span className="label-title">
//                                         <em>*</em> 사업자등록증
//                                     </span>
//                                 </div>
//                                 <FileUpload
//                                     uploadType="BIZ_LIC"
//                                     existingUpload={existingUpload}
//                                     buttonText="사진 업로드"
//                                     onFileChange={(e, type) => handleCommonFileChange(e, type, setFormData, uploadFile)}
//                                 />
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             <div className="page-footer">
//                 <div className="form-button">
//                     <div className="button-wrapper">
//                         <button className="btn btn-md btn-primary" onClick={handleNext}>
//                             다음
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

// =======================================
// Step 7: ContractSigning
// =======================================
const ContractSigning = ({ contractId, formData, handleChange, setFormData, prevStep, nextStep, canGoNext }) => {
    const drivers = formData?.contract?.contractDrivers || [];
    const rangeText = driverRangeText(drivers);
    const youngestAge = computeYoungestAge(drivers);

    const sigCanvasRef = useRef(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const pdfUrl = formData?.contract?.originContractUpload?.objectUrl;

    // 파일 업로드 & 싸인
    const [uploadFile] = useUploadFileMutation();

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    useEffect(() => {
        if (sigCanvasRef.current) {
            setTimeout(() => {
                sigCanvasRef.current._resizeCanvas();
                if (formData.signedImage) {
                    sigCanvasRef.current.fromDataURL(formData.signedImage);
                }
            }, 200);
        }
    }, [formData.signedImage]);

    // "서명하기" → SignaturePadPopup 표시
    const handleSign = () => {
        setFormData((prev) => ({
            ...prev,
            signedImage: null,
            signaturePadPopupStatus: true,
        }));
    };

    // Base64 -> File 변환 헬퍼
    function base64ToFile(base64Data, fileName) {
        const arr = base64Data.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    }

    // "다음" 버튼 → 1) 파일 업로드 → 2) signContract API
    const handleNext = async () => {
        if (!canGoNext) return;

        try {
            if (!formData.signedImage) {
                setFormData((prev) => ({ ...prev, toastMsg: "서명 이미지가 없습니다." }));
                return;
            }

            // 이미 업로드된 signUploadId가 없으면 업로드 진행
            if (!formData.signUploadId) {
                const file = base64ToFile(formData.signedImage, "signature.png");
                const formDataObj = new FormData();
                formDataObj.append("file", file);

                const uploadResp = await uploadFile(formDataObj).unwrap();
                const signUploadId = uploadResp.data?.id;
                if (!signUploadId) {
                    throw new Error("파일 업로드 실패: signUploadId를 찾을 수 없음");
                }
                setFormData((prev) => ({ ...prev, signUploadId }));
            }

            nextStep();
        } catch (error) {
            console.error("서명 처리 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "서명 업로드/처리에 실패했습니다.",
            }));
        }
    };

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={prevStep}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">계약정보 확인</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="contract-info">
                            <div className="info-list">
                                <div className="item">
                                    <span className="item-tit">계약자</span>
                                    <span className="item-desc">{formData?.name}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">대여차량</span>
                                    <span className="item-desc">{`${formData?.contract.contractCar.manufacturerName || ""} ${
                                        formData?.contract.contractCar.modelDetailName || ""
                                    } ${formData?.contract.contractCar.className || ""} ${
                                        formData?.contract.contractCar.trimName || ""
                                    }`}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">차량번호</span>
                                    <span className="item-desc">{formData?.contract.contractCar.carNumber}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">연식</span>
                                    <span className="item-desc">{formData?.contract.contractCar.firstDate}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">운전자 범위</span>
                                    <span className="item-desc">{rangeText}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">운전자 연령</span>
                                    <span className="item-desc">{youngestAge}</span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">대여 시작일시</span>
                                    <span className="item-desc">
                                        {dateFormatDateTime(formData?.contract.allocation?.releasedAt, "YYYY-MM-DD HH:mm")}
                                    </span>
                                </div>
                                <div className="item">
                                    <span className="item-tit">탁송 주소</span>
                                    <span className="item-desc">{`${formData?.contract.deliveryAddress || ""} ${
                                        formData?.contract.deliveryAddressDetail || ""
                                    }`}</span>
                                </div>
                            </div>
                        </div>

                        {/* 현재 미서명 상태인 경우 아래 노출 안함 */}
                        {canGoNext && (
                            <div className="singning-preview">
                                <div className="preview-head">
                                    <span className="head-tit">
                                        전자서명 <em>*계약 내용을 확인해주세요.</em>
                                    </span>
                                </div>
                                <div className="preview-content">
                                    <ReactSignatureCanvas
                                        ref={sigCanvasRef}
                                        canvasProps={{
                                            style: { width: "100%", height: "152px" },
                                        }}
                                        clearOnResize={false}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button className={`btn btn-md button-login ${canGoNext ? "btn-fourth" : "btn-primary"}`} onClick={handleSign}>
                            {canGoNext ? "재서명" : "서명하기"}
                        </button>
                    </div>
                    {canGoNext && (
                        <div className="button-wrapper">
                            <button
                                className={`btn btn-md button-login ${canGoNext ? "btn-primary" : "btn-disabled"}`}
                                onClick={handleNext}
                            >
                                다음
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 8: StrictIdentificationCheck
// =======================================
const StrictIdentificationCheck = ({ contractId, formData, handleChange, setFormData, nextStep, prevStep }) => {
    const timerRef = useRef(null);
    const [nicePhoneRequest] = useNicePhoneRequestMutation();
    const [nicePhoneVerify] = useNicePhoneVerifyMutation();
    const [signContract] = useSignContractMutation();
    const mainDriver = formData.contract.contractDrivers?.find((d) => d.contractDriverType === "MAIN");

    const startTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setFormData((prev) => ({ ...prev, timer: 180, authCodeStatus: AuthCodeStatus.IN_PROGRESS }));
        timerRef.current = setInterval(() => {
            setFormData((prev) => {
                if (prev.timer <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return { ...prev, timer: 0, authCodeStatus: AuthCodeStatus.SIGN_TIMEOUT };
                }
                return { ...prev, timer: prev.timer - 1 };
            });
        }, 1000);
    }, [setFormData]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const validateForm = () => {
        if (!formData.identityAuthTerms) {
            setFormData((prev) => ({ ...prev, toastMsg: "본인 확인 서비스 이용약관 전체동의에 동의해주세요." }));
            return false;
        }
        return true;
    };

    const handleRequestAuth = async () => {
        // if (!formData.name || !mainDriver.idNumber) {
        //     setFormData((prev) => ({
        //         ...prev,
        //         toastMsg: "이름, 주민번호를 모두 입력해주세요.",
        //     }));
        //     return;
        // }
        if (!formData.mobileType || !formData.phone) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "통신사, 휴대폰번호를 모두 입력해주세요.",
            }));
            return;
        }
        try {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            const resp = await nicePhoneRequest({
                // idNumber: formData.idNumber?.substring(0, 7), // 주민번호 앞 7자리만 전달 (6자리 + 뒷자리 첫번째)
                idNumber: mainDriver.idNumber?.substring(0, 7), // 주민번호 앞 7자리만 전달 (6자리 + 뒷자리 첫번째)
                mobileType: formData.mobileType,
                phone: formData.phone,
                name: formData.name,
            }).unwrap();
            setFormData((prev) => ({
                ...prev,
                phoneAuthId: resp.data?.id || 0,
                authVerified: false,
                authNum: "",
                toastMsg: "인증번호가 발송되었습니다.",
                authCodeStatus: AuthCodeStatus.WAITING,
            }));
            startTimer();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "인증번호 요청에 실패했습니다.",
            }));
        }
    };

    // 인증 완료 버튼: authVerified가 true여야 활성화되고, 누르면 signContract API 호출 후 nextStep() 실행
    const handleNext = async () => {
        if (!validateForm()) return;

        try {
            // contractDrivers의 driverLicenseNumber를 localCode와 합쳐서 처리
            const processedContractDrivers = formData.contract?.contractDrivers?.map((driver) => {
                let updated = { ...driver };
                if (updated.driverLicenseNumber && updated.localCode) {
                    updated.driverLicenseNumber = `${updated.localCode}${updated.driverLicenseNumber}`;
                }
                return updated;
            });

            const reqBody = {
                ...formData.contract,
                contractDrivers: processedContractDrivers,
            };

            // 사고차량 이미지들을 API 요청 형식에 맞게 변환
            const accidentCarImages =
                formData.contract?.contractGroup?.carImages
                    ?.filter((img) => img.carImageType === "ACCIDENT_CAR")
                    ?.map((img) => ({
                        upload: {
                            id: img.upload?.id || 0,
                        },
                        carImageType: "ACCIDENT_CAR",
                    })) || [];

            const requestBody = {
                signUploadId: formData.signUploadId || 0,
                authAbleYn: formData.authAbleYn === true ? "Y" : "N",
                phoneAuthId: formData.phoneAuthId || null,
                contractDrivers: processedContractDrivers,
                allocation: formData.contract?.allocation || null,
                deliveryAddress: formData.contract?.deliveryAddress,
                deliveryAddressDetail: formData.contract?.deliveryAddressDetail,
                deliveryAddressBCode: formData.contract?.deliveryAddressBCode,
                contractBusinessInfo: formData.contract?.contractGroup?.contractBusinessInfo || null,
                accidentCar: formData.contract?.contractGroup?.accidentCar || null,
                contractUploads:
                    formData.contract?.contractGroup?.contractUploads?.filter((u) =>
                        ["DRIVER_LICENSE_MAIN", "DRIVER_LICENSE_SUB", "BIZ_LIC"].includes(u.uploadType)
                    ) || [],
                carImages: accidentCarImages,
            };

            await signContract({ contractId, body: requestBody }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "전자계약 서명이 완료되었습니다.",
            }));
            nextStep();
        } catch (error) {
            console.error("서명 처리 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "전자계약 서명이 실패했습니다.",
            }));
        }
    };

    // 이전 버튼 클릭 시 타이머와 인증 상태 초기화
    const handlePrev = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setFormData((prev) => ({
            ...prev,
            timer: 0,
            authCodeStatus: AuthCodeStatus.WAITING,
            authNum: "",
            phoneAuthId: null,
            authVerified: false,
        }));
        prevStep();
    };

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-nav_before" onClick={handlePrev}>
                    <img src={beforeIcon} alt="" />
                </button>
                <span className="header-tit">본인인증</span>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-head_main" style={{ marginBottom: "0" }}>
                        <span className="head-tit">본인인증</span>
                        <div className="term-container" style={{ minHeight: "auto" }}>
                            <div className="term-content">
                                <div className="term-list term-all" style={{ marginBottom: "0", paddingBottom: "0" }}>
                                    <div className="term-item">
                                        <div className="checkbox">
                                            <input
                                                type="checkbox"
                                                id="identityAuthTerms"
                                                name="identityAuthTerms"
                                                checked={formData.identityAuthTerms}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, identityAuthTerms: e.target.checked }))}
                                            />
                                            <label htmlFor="identityAuthTerms">
                                                <span>본인 확인 서비스 이용약관 전체동의</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-wrapper">
                        {formData.authAbleYn === true && (
                            <>
                                {/* 이름 */}
                                <div className="form-row">
                                    <div className="row-item">
                                        <div className="item-label">
                                            <span className="label-title">이름</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="content-field">
                                                <div className="field-row">
                                                    <input
                                                        type="text"
                                                        placeholder="이름을 입력해주세요."
                                                        name="name"
                                                        value={mainDriver.name}
                                                        onChange={handleChange}
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* 주민번호 */}
                                <div className="form-row">
                                    <div className="row-item">
                                        <div className="item-label">
                                            <span className="label-title">주민번호</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="content-field">
                                                <div className="field-row">
                                                    <input
                                                        type="text"
                                                        placeholder="주민등록번호 13자리 입력"
                                                        name="idNumber"
                                                        value={formatIdNumberBackNumber(mainDriver.idNumber)}
                                                        onChange={handleChange}
                                                        onInput={onlyNumericInput}
                                                        maxLength={14}
                                                        disabled
                                                    />
                                                </div>
                                                <div className="field-row" style={{ width: "72px", flexShrink: "0" }}>
                                                    <input type="text" name="calculatedAge" value={computeAgeText(mainDriver)} disabled />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* 휴대폰 번호 */}
                                <div className="form-row">
                                    <div className="row-item">
                                        <div className="item-label">
                                            <span className="label-title">휴대폰 번호</span>
                                        </div>
                                        <div className="item-content">
                                            <div className="content-field">
                                                <div className="field-row" style={{ width: "80px", flexShrink: "0" }}>
                                                    <Select
                                                        options={mobileTypeOptions}
                                                        className="custom-select"
                                                        classNamePrefix="custom-select"
                                                        isSearchable={false}
                                                        placeholder="통신사"
                                                        value={mobileTypeOptions.find((option) => option.value === formData.mobileType)}
                                                        onChange={(option) =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                mobileType: option.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                                <div className="field-row">
                                                    <input
                                                        type="text"
                                                        placeholder="-없이 휴대폰번호 입력"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        onInput={onlyNumericInput}
                                                        disabled
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-fourth"
                                                    onClick={handleRequestAuth}
                                                    disabled={formData.authVerified}
                                                >
                                                    {formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS ? "재발송" : "발송"}
                                                </button>
                                            </div>
                                            {/* 인증번호 입력칸 */}
                                            {formData.authCodeStatus !== AuthCodeStatus.WAITING && (
                                                <div className="item-content">
                                                    <div className="content-field">
                                                        <div className="field-row">
                                                            <input
                                                                type="text"
                                                                placeholder="인증번호를 입력해주세요."
                                                                name="authNum"
                                                                value={formData.authNum}
                                                                onChange={handleChange}
                                                                onInput={onlyNumericInput}
                                                            />
                                                            <span className="input-esset color-error">
                                                                {formData.timer > 0 ? formatTime(formData.timer) : "만료"}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-primary"
                                                            onClick={async () => {
                                                                if (!formData.authNum) {
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        toastMsg: "인증번호를 입력해주세요.",
                                                                    }));
                                                                    return;
                                                                }
                                                                if (!formData.phoneAuthId) {
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        toastMsg: "인증번호 요청을 먼저 해주세요.",
                                                                    }));
                                                                    return;
                                                                }
                                                                try {
                                                                    await nicePhoneVerify({
                                                                        id: formData.phoneAuthId,
                                                                        authNum: formData.authNum,
                                                                    }).unwrap();
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        authVerified: true,
                                                                        toastMsg: "본인 인증이 완료되었습니다.",
                                                                    }));
                                                                    if (timerRef.current) {
                                                                        clearInterval(timerRef.current);
                                                                        timerRef.current = null;
                                                                    }
                                                                } catch (error) {
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        toastMsg: error.message || "인증에 실패했습니다.",
                                                                    }));
                                                                }
                                                            }}
                                                            disabled={formData.authVerified}
                                                        >
                                                            인증번호 확인
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="form-row">
                            <div className="row-item">
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-check">
                                            <div className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="authAbleYn"
                                                    name="authAbleYn"
                                                    checked={!formData.authAbleYn}
                                                    onChange={(e) => setFormData((prev) => ({ ...prev, authAbleYn: !e.target.checked }))}
                                                />
                                                <label htmlFor="authAbleYn">
                                                    <span>본인인증 불가 고객</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <p className="txt-warning">본인인증이 진행되지 않은 고객입니다. 확인후 출고 하시기 바랍니다.</p> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-footer">
                <div className="form-button">
                    <div className="button-wrapper">
                        <button
                            className="btn btn-md btn-primary"
                            onClick={handleNext}
                            disabled={!(formData.authVerified || !formData.authAbleYn)}
                        >
                            인증 완료
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step 9: ContractComplete
// =======================================
const ContractComplete = ({ formData, navigate }) => {
    const closeBrowser = () => {
        window.close();
    };

    return (
        <div className="app-mobile">
            <div className="page-header" style={{ border: "none" }}>
                <button type="button" className="btn-browser_close" onClick={closeBrowser}>
                    <img src={closeIcon} alt="" />
                </button>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-description">
                            <div className="description-img">
                                <img src={successImg} alt="" />
                            </div>
                            <span className="description-sub_txt">
                                <b>{formData.name || ""}</b> 고객님 <br />
                                감사합니다 :)
                            </span>
                            <p>
                                계약서 서명이 완료 되었습니다. <br /> 렌터카 확인 후 최종 계약서를 전달 드리겠습니다. <br />
                                잠시만 기다려주세요.
                            </p>
                        </div>
                    </div>
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button
                                className="btn btn-fourth btn-md button-login"
                                onClick={() => (window.location.href = `tel:${formData?.rentCorp?.phone}`)}
                            >
                                렌터카 연락
                            </button>
                            <button
                                className="btn btn-primary btn-md button-login"
                                onClick={() => (window.location.href = "tel:1800-6586")}
                            >
                                모빌리카 연락
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step SIGN_TIMEOUT: ContractTimeout
// =======================================
const ContractTimeout = ({ navigate, prevStep }) => {
    const closeBrowser = () => {
        window.close();
    };

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-browser_close" onClick={closeBrowser}>
                    <img src={closeIcon} alt="" />
                </button>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-description">
                            <div className="description-img">
                                <img src={timeoutImg} alt="" />
                            </div>
                            <span className="description-sub_txt">
                                <b>계약 서명 시간 초과</b>
                            </span>
                            <p>
                                전자계약 서명 시간이 만료되었습니다. <br />
                                렌터카사로 연락하여 다시 발송 받으시길 바랍니다.
                            </p>
                        </div>
                    </div>
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-primary btn-md button-login">렌터카 연락</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Step NOT_FOUND: ContractNotFound
// =======================================
const ContractNotFound = ({ navigate, prevStep }) => {
    const closeBrowser = () => {
        window.close();
    };

    return (
        <div className="app-mobile">
            <div className="page-header">
                <button type="button" className="btn-browser_close" onClick={closeBrowser}>
                    <img src={closeIcon} alt="" />
                </button>
            </div>
            <div className="page-content">
                <div className="content-form">
                    <div className="form-wrapper">
                        <div className="form-description">
                            <div className="description-img">
                                <img src={errorIcon} alt="" />
                            </div>
                            <span className="description-sub_txt">
                                <b>계약 찾을 수 없음</b>
                            </span>
                            <p>
                                계약을 찾을 수 없습니다. <br />
                                렌터카사로 연락하여 다시 발송 받으시길 바랍니다.
                            </p>
                        </div>
                    </div>
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-primary btn-md button-login">렌터카 연락</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =======================================
// Signing Popup
// =======================================
const SignaturePadPopup = ({ layer = true, onClose, formData, setFormData }) => {
    const sigCanvasRef = useRef(null);

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    useEffect(() => {
        if (sigCanvasRef.current) {
            setTimeout(() => {
                sigCanvasRef.current._resizeCanvas();
                if (formData.signedImage) {
                    sigCanvasRef.current.fromDataURL(formData.signedImage);
                }
            }, 200);
        }
    }, [formData.signedImage]);

    const handleClear = () => {
        if (sigCanvasRef.current) {
            sigCanvasRef.current.clear();
        }
    };

    const handleConfirm = () => {
        if (sigCanvasRef.current && sigCanvasRef.current.isEmpty()) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "서명을 입력해주세요.",
            }));
            return;
        }
        const dataURL = sigCanvasRef.current.getCanvas().toDataURL("image/png");
        setFormData((prev) => ({
            ...prev,
            signedImage: dataURL || null,
        }));
        onClose();
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup signing-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>계약서 서명</span>
                    </div>
                    <div className="content-description">
                        <p>계약서에 서명을 입력해주세요.</p>
                    </div>
                    <div className="content-sign-pad">
                        <div className="pad-wrapper" style={{ width: "100%", height: "152px" }}>
                            <ReactSignatureCanvas
                                ref={sigCanvasRef}
                                canvasProps={{
                                    style: { width: "100%", height: "152px" },
                                }}
                                clearOnResize={false}
                            />
                        </div>
                        <button type="button" className="btn-reset_pad" onClick={handleClear}>
                            초기화
                        </button>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-close" onClick={handleConfirm}>
                        확인
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

// =======================================
// Terms Popup
// =======================================
const TermViewPopup = ({ layer = true, onClose, formData }) => {
    const [numPages, setNumPages] = useState(0);

    useEffect(() => {
        // 스크롤 잠금
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup mobile-term-popup">
                <div className="popup-head">
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="pdf-container">
                        {/* 약관 PDF 뷰어 */}
                        <Document file={formData.selectedTermPdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                            {Array.from({ length: numPages }, (_, index) => (
                                <div key={index} className="pdf-page">
                                    <Page pageNumber={index + 1} />
                                </div>
                            ))}
                        </Document>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-confirm" onClick={onClose}>
                        확인
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

// =======================================
// Car Images Popup
// =======================================
const CarImagesPopup = ({ layer = true, onClose, formData }) => {
    useEffect(() => {
        // 스크롤 잠금
        document.body.classList.add("scroll-lock");

        // 각 이미지에 PinchZoom 적용
        // const images = document.querySelectorAll(".car-image-wrapper");
        // const pinchZoomInstances = [];

        // images.forEach((image) => {
        //     const pinchZoom = new PinchZoom(image, {
        //         draggable: true,
        //         maxZoom: 5,
        //     });
        //     pinchZoomInstances.push(pinchZoom);
        // });

        return () => {
            document.body.classList.remove("scroll-lock");
            // 인스턴스 정리
            // pinchZoomInstances.forEach((instance) => {
            //     instance.destroy();
            // });
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup mobile-car-images-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <div className="tit-main">{`${formData?.contract.contractCar.carNumber} (${formData?.contract.contractCar.color})`}</div>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="car-images-container">
                        {formData?.contract.carImages.map((img, idx) => (
                            <div key={img.id || idx} className="car-image">
                                <div className="car-image-wrapper">
                                    <img
                                        src={img.upload?.objectUrl || img.upload?.url}
                                        alt={`car-${idx + 1}`}
                                        style={{ maxWidth: "100%", height: "auto" }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

// =======================================
// File Upload Component
// =======================================
const FileUpload = ({ buttonText, onFileChange, uploadType, existingUpload }) => {
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    useEffect(() => {
        if (existingUpload && existingUpload.upload && existingUpload.upload.objectUrl) {
            setPreviewUrl(existingUpload.upload.objectUrl);
        }
    }, [existingUpload]);

    const handleFileUploadClick = () => {
        fileInputRef.current.removeAttribute("capture");
        fileInputRef.current.click();
    };

    const handleCameraClick = () => {
        fileInputRef.current.setAttribute("capture", "environment");
        fileInputRef.current.click();
    };

    const handleChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type === "application/pdf") {
                setPreviewUrl(URL.createObjectURL(file));
            } else {
                setPreviewUrl(URL.createObjectURL(file));
            }
            onFileChange(e, uploadType);
        }
    };

    return (
        <div className="item-content">
            <div className="content-field">
                <div className="button-group" style={{ display: "flex", gap: "8px" }}>
                    <button type="button" onClick={handleFileUploadClick} className="btn btn-xs btn-fourth">
                        {buttonText || "사진 업로드"}
                    </button>
                    {/* {isMobile && (
                        <button type="button" onClick={handleCameraClick} className="btn btn-xs btn-fourth">
                            카메라 촬영
                        </button>
                    )} */}
                </div>
                <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".pdf,.jpg,.png,.jpeg" onChange={handleChange} />
            </div>
            {previewUrl && (
                <div className="content-field">
                    <div className="file-preview">
                        <img src={previewUrl} alt="미리보기" />
                    </div>
                </div>
            )}
        </div>
    );
};

// =======================================
// Accident Car Image Upload Component
// =======================================
const AccidentCarImageUpload = ({ formData, setFormData, uploadFile }) => {
    const fileInputRef = useRef(null);

    // 현재 업로드된 사고차량 이미지들 가져오기
    const accidentCarImages = formData?.contract?.contractGroup?.carImages?.filter((img) => img.carImageType === "ACCIDENT_CAR") || [];

    const handleFileSelect = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        // 현재 이미지 개수 + 새로 선택한 파일 개수가 20개를 초과하는지 확인
        if (accidentCarImages.length + files.length > 20) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: `최대 20개까지만 첨부 가능합니다. (현재: ${accidentCarImages.length}개)`,
            }));
            return;
        }

        // 파일 형식 체크 (png, jpg, jpeg만 허용)
        const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
        const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));

        if (invalidFiles.length > 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "png, jpg, jpeg 파일만 첨부 가능합니다.",
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, isLoading: true }));

        try {
            // 각 파일을 순차적으로 업로드
            const uploadPromises = files.map(async (file) => {
                const formDataObj = new FormData();
                formDataObj.append("file", file);
                const response = await uploadFile(formDataObj).unwrap();
                return {
                    carImageType: "ACCIDENT_CAR",
                    upload: {
                        id: response.data?.id || 0,
                        fileName: file.name,
                        objectUrl: response.data?.objectUrl,
                    },
                };
            });

            const newImages = await Promise.all(uploadPromises);

            setFormData((prev) => {
                const existingCarImages = prev.contract?.contractGroup?.carImages || [];
                const updatedCarImages = [...existingCarImages, ...newImages];

                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        contractGroup: {
                            ...prev.contract?.contractGroup,
                            carImages: updatedCarImages,
                        },
                    },
                    isLoading: false,
                };
            });
        } catch (error) {
            console.error("파일 업로드 에러:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "파일 업로드에 실패했습니다.",
                isLoading: false,
            }));
        }

        // 파일 input 초기화
        e.target.value = "";
    };

    const handleImageRemove = (indexToRemove) => {
        setFormData((prev) => {
            const updatedCarImages =
                prev.contract?.contractGroup?.carImages?.filter((_, index) => {
                    if (_.carImageType === "ACCIDENT_CAR") {
                        const accidentCarIndex = prev.contract.contractGroup.carImages
                            .filter((img) => img.carImageType === "ACCIDENT_CAR")
                            .indexOf(_);
                        return accidentCarIndex !== indexToRemove;
                    }
                    return true;
                }) || [];

            return {
                ...prev,
                contract: {
                    ...prev.contract,
                    contractGroup: {
                        ...prev.contract?.contractGroup,
                        carImages: updatedCarImages,
                    },
                },
            };
        });
    };

    return (
        <div style={{ width: "100%" }}>
            {/* 파일 선택 버튼 */}
            <div style={{ marginBottom: "15px" }}>
                <button
                    type="button"
                    onClick={handleFileSelect}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                    }}
                >
                    이미지 선택 ({accidentCarImages.length}/20)
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept=".png,.jpg,.jpeg"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
            </div>

            {/* 썸네일 이미지 목록 */}
            {accidentCarImages.length > 0 && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: "10px",
                        marginTop: "15px",
                    }}
                >
                    {accidentCarImages.map((image, index) => (
                        <div
                            key={index}
                            style={{
                                position: "relative",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                overflow: "hidden",
                                backgroundColor: "#f9f9f9",
                            }}
                        >
                            <img
                                src={image.upload?.objectUrl}
                                alt={`사고차량 이미지 ${index + 1}`}
                                style={{
                                    width: "100%",
                                    height: "80px",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    // 이미지 클릭 시 새 창에서 확대 보기
                                    window.open(image.upload?.objectUrl, "_blank");
                                }}
                            />
                            {/* 삭제 버튼 */}
                            <button
                                type="button"
                                onClick={() => handleImageRemove(index)}
                                style={{
                                    position: "absolute",
                                    top: "2px",
                                    right: "2px",
                                    width: "20px",
                                    height: "20px",
                                    backgroundColor: "rgba(255, 0, 0, 0.7)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "50%",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                title="이미지 삭제"
                            >
                                ×
                            </button>
                            {/* 파일명 표시 */}
                            <div
                                style={{
                                    padding: "5px",
                                    fontSize: "10px",
                                    color: "#666",
                                    textAlign: "center",
                                    backgroundColor: "white",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {image.upload?.fileName || `이미지 ${index + 1}`}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 이미지가 없을 때 안내 메시지 */}
            {accidentCarImages.length === 0 && (
                <div
                    style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                        backgroundColor: "#f9f9f9",
                        border: "1px dashed #ddd",
                        borderRadius: "4px",
                        lineHeight: "1.28",
                    }}
                >
                    사고차량 이미지를 선택해주세요.
                </div>
            )}
        </div>
    );
};
